/* hooks/useRegistrySelection — общий режим множественного выбора реестров
   Кухни (ТТК, Сырьё, Меню). Выносит дублировавшуюся в трёх реестрах механику
   selectMode/selected + защиты:
   - prune: после фонового reload чистим выбор от исчезнувших id (иначе счётчик
     врёт и bulk работает по призракам);
   - reset: при смене resetKey (вкладка/пространство) выходим из режима выбора. */

import React from 'react';

  const { useState, useEffect, useCallback, useMemo } = React;

  // items — текущий видимый список; idOf — извлечь id элемента; resetKey —
  // значение, при смене которого режим выбора сбрасывается (напр. kind вкладки).
  function useRegistrySelection(items, { idOf = (it) => it.id, resetKey } = {}) {
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    const ids = useMemo(() => (items || []).map(idOf), [items, idOf]);

    // Сброс при смене resetKey (вкладка/пространство).
    useEffect(() => { setSelectMode(false); setSelected(new Set()); setBusy(false); }, [resetKey]);

    // Prune выбора по актуальному списку (фоновое удаление/reload).
    useEffect(() => {
      const live = new Set(ids);
      setSelected((prev) => {
        if (prev.size === 0) return prev;
        let changed = false;
        const next = new Set();
        prev.forEach((id) => { if (live.has(id)) next.add(id); else changed = true; });
        return changed ? next : prev;
      });
    }, [ids]);

    const toggle = useCallback((id) => setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    }), []);

    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    const selectAll = useCallback(() => {
      setSelected(allSelected ? new Set() : new Set(ids));
    }, [allSelected, ids]);

    const exit = useCallback(() => { setSelectMode(false); setSelected(new Set()); }, []);

    return {
      selectMode, setSelectMode, selected, busy, setBusy,
      toggle, allSelected, selectAll, exit,
      count: selected.size,
    };
  }

export { useRegistrySelection };
